import React, { useState } from "react";
import axios from "axios";
import { motion } from "framer-motion";
import { ArrowUpRight, Check, Loader2 } from "lucide-react";
import { toast } from "sonner";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../ui/select";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const CATEGORY_OPTS = [
  "Pipe", "Valves", "Fittings", "Flanges", "Gaskets",
  "Stud Bolts", "Blinds", "Fasteners", "Lumber / Plywood", "Hardware", "Other",
];

const TIMELINES = ["ASAP / Rush", "Within 1 week", "2–4 weeks", "Planning stage"];

const fieldBase =
  "w-full bg-card border border-border rounded-none px-4 py-3 text-foreground placeholder:text-muted-foreground focus:outline-none focus:border-primary focus:ring-1 focus:ring-primary transition-colors";

export const QuoteForm = () => {
  const [form, setForm] = useState({
    company: "", contact_name: "", email: "", phone: "",
    project_location: "", items: "", quantity: "", timeline: "", notes: "",
  });
  const [categories, setCategories] = useState([]);
  const [loading, setLoading] = useState(false);
  const [done, setDone] = useState(false);

  const set = (k) => (e) => setForm((f) => ({ ...f, [k]: e.target.value }));

  const toggleCat = (c) =>
    setCategories((prev) => (prev.includes(c) ? prev.filter((x) => x !== c) : [...prev, c]));

  const submit = async (e) => {
    e.preventDefault();
    if (!form.company || !form.contact_name || !form.email || !form.items) {
      toast.error("Please fill in company, contact, email and what you need.");
      return;
    }
    setLoading(true);
    try {
      await axios.post(`${API}/quotes`, { ...form, categories });
      setDone(true);
      toast.success("Quote request received — we'll be in touch shortly.");
      setForm({ company: "", contact_name: "", email: "", phone: "", project_location: "", items: "", quantity: "", timeline: "", notes: "" });
      setCategories([]);
    } catch (err) {
      toast.error("Something went wrong. Please try again or email us directly.");
    } finally {
      setLoading(false);
    }
  };

  return (
    <section id="contact" className="relative py-24 md:py-36 bg-card border-t border-border" data-testid="quote-form-section">
      <div className="max-w-[1500px] mx-auto px-5 md:px-10 grid lg:grid-cols-[0.9fr_1.1fr] gap-14 lg:gap-20">
        {/* Left intro */}
        <div>
          <span className="font-mono-tech text-xs tracking-[0.3em] uppercase text-primary flex items-center gap-3">
            <span className="inline-block w-10 h-px bg-accent" /> Request a Quote
          </span>
          <h2 className="font-display font-extrabold uppercase tracking-tight text-4xl md:text-6xl mt-6 leading-[0.9] text-foreground">
            Tell us what<br />you need
          </h2>
          <p className="mt-6 max-w-md text-base md:text-lg text-steel leading-relaxed">
            Send over your list, spec or bill of materials. We'll source it, verify
            the specs and get an accurate quote back to you fast.
          </p>

          <div className="mt-10 space-y-5 border-t border-border pt-8">
            {[
              ["Email", "estimating@anchorpointind.com"],
              ["Phone", "956.844.0676"],
              ["Coverage", "Gulf Coast · Nationwide shipping"],
            ].map(([k, v]) => (
              <div key={k} className="flex justify-between items-baseline gap-4">
                <span className="font-mono-tech text-[10px] tracking-[0.25em] uppercase text-steel">{k}</span>
                <span className="text-foreground text-sm md:text-base text-right">{v}</span>
              </div>
            ))}
          </div>
        </div>

        {/* Form */}
        <motion.div
          initial={{ opacity: 0, y: 30 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true, margin: "-60px" }}
          transition={{ duration: 0.7, ease: [0.22, 1, 0.36, 1] }}
          className="bg-background border border-border p-6 md:p-10"
        >
          {done ? (
            <div className="min-h-[400px] flex flex-col items-center justify-center text-center" data-testid="quote-success">
              <div className="w-16 h-16 border border-primary flex items-center justify-center text-primary mb-6">
                <Check size={28} />
              </div>
              <h3 className="font-display font-bold text-3xl uppercase text-foreground">Request received</h3>
              <p className="mt-3 text-steel max-w-sm">Thanks — an Anchor Point specialist will review your request and follow up shortly.</p>
              <button onClick={() => setDone(false)} data-testid="new-quote-btn" className="mt-8 font-mono-tech text-xs tracking-[0.2em] uppercase text-primary hover:text-accent transition-colors">
                Submit another request →
              </button>
            </div>
          ) : (
            <form onSubmit={submit} className="space-y-6" data-testid="quote-form">
              <div className="grid md:grid-cols-2 gap-5">
                <Field label="Company *"><input data-testid="input-company" className={fieldBase} value={form.company} onChange={set("company")} placeholder="Your company" /></Field>
                <Field label="Contact Name *"><input data-testid="input-contact" className={fieldBase} value={form.contact_name} onChange={set("contact_name")} placeholder="Full name" /></Field>
                <Field label="Email *"><input data-testid="input-email" type="email" className={fieldBase} value={form.email} onChange={set("email")} placeholder="you@company.com" /></Field>
                <Field label="Phone"><input data-testid="input-phone" className={fieldBase} value={form.phone} onChange={set("phone")} placeholder="(000) 000-0000" /></Field>
              </div>

              <Field label="Project Location">
                <input data-testid="input-location" className={fieldBase} value={form.project_location} onChange={set("project_location")} placeholder="City, State / Jobsite" />
              </Field>

              <div>
                <span className="font-mono-tech text-[10px] tracking-[0.25em] uppercase text-steel">Categories</span>
                <div className="mt-3 flex flex-wrap gap-2.5" data-testid="category-group">
                  {CATEGORY_OPTS.map((c) => {
                    const active = categories.includes(c);
                    return (
                      <button
                        type="button"
                        key={c}
                        onClick={() => toggleCat(c)}
                        data-testid={`cat-${c}`}
                        className={`inline-flex items-center gap-2 border px-3 py-2 font-mono-tech text-[11px] uppercase tracking-wide transition-colors ${
                          active ? "border-primary bg-primary/15 text-foreground" : "border-border text-steel hover:border-steel"
                        }`}
                      >
                        <span className={`w-3.5 h-3.5 border flex items-center justify-center shrink-0 ${active ? "bg-primary border-primary" : "border-steel"}`}>
                          {active && <Check size={10} className="text-primary-foreground" />}
                        </span>
                        {c}
                      </button>
                    );
                  })}
                </div>
              </div>

              <div className="grid md:grid-cols-2 gap-5">
                <Field label="Quantity / Scale">
                  <input data-testid="input-quantity" className={fieldBase} value={form.quantity} onChange={set("quantity")} placeholder="e.g. 200 ft, 50 units" />
                </Field>
                <Field label="Timeline">
                  <Select value={form.timeline} onValueChange={(v) => setForm((f) => ({ ...f, timeline: v }))}>
                    <SelectTrigger data-testid="select-timeline" className="rounded-none h-[50px] bg-card border-border">
                      <SelectValue placeholder="Select timeline" />
                    </SelectTrigger>
                    <SelectContent className="rounded-none">
                      {TIMELINES.map((t) => (
                        <SelectItem key={t} value={t} className="rounded-none font-mono-tech text-xs">{t}</SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </Field>
              </div>

              <Field label="What do you need? *">
                <textarea data-testid="input-items" rows={4} className={fieldBase + " resize-none"} value={form.items} onChange={set("items")} placeholder="List items, specs, grades, sizes, or paste your BOM…" />
              </Field>

              <Field label="Additional Notes">
                <textarea data-testid="input-notes" rows={2} className={fieldBase + " resize-none"} value={form.notes} onChange={set("notes")} placeholder="Anything else we should know" />
              </Field>

              <button
                type="submit"
                disabled={loading}
                data-testid="quote-submit-button"
                className="group w-full inline-flex items-center justify-center gap-3 bg-accent text-accent-foreground px-8 py-4 font-mono-tech text-xs tracking-[0.2em] uppercase hover:bg-foreground hover:text-background transition-colors duration-300 disabled:opacity-60"
              >
                {loading ? <Loader2 size={16} className="animate-spin" /> : <>Submit Request <ArrowUpRight size={16} className="group-hover:translate-x-1 group-hover:-translate-y-1 transition-transform" /></>}
              </button>
            </form>
          )}
        </motion.div>
      </div>
    </section>
  );
};

const Field = ({ label, children }) => (
  <label className="block">
    <span className="font-mono-tech text-[10px] tracking-[0.25em] uppercase text-steel">{label}</span>
    <div className="mt-2">{children}</div>
  </label>
);
